import React, { createContext, useState } from "react";
import { Box, Container, useColorMode } from "@chakra-ui/react";
import { NavbarWithCallToAction } from "./chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "./chakra/FooterWithFourColumns/FooterWithFourColumns";
import { ModuleNavLauncher } from "./ModuleNavLauncher";
import { ModuleRouteLayoutOptions } from "../types/ModuleConfig";
import { getColor } from "../brandConfig";
import { useAuth } from "../contexts/AuthContext";

/**
 * Runtime chrome control for pages inside ModulePageLayout. Lets a page hide
 * the breadcrumb bar dynamically (e.g. the calendar's Fit mode reclaims that
 * strip for the grid). Static hiding stays on the route's `noBreadcrumb`.
 */
export const LayoutChromeContext = createContext<{
  setBreadcrumbHidden: (hidden: boolean) => void;
}>({ setBreadcrumbHidden: () => {} });

interface ModulePageLayoutProps {
  moduleConfig: { id: string; name: string; navigation: any[]; [key: string]: any };
  layoutOptions?: ModuleRouteLayoutOptions;
  children: React.ReactNode;
}

/**
 * ModulePageLayout - Wraps page content with navbar, breadcrumb, container, and footer.
 *
 * Used automatically by App.tsx when a route has `useLayout: true` in moduleConfig.
 * Page components return ONLY their content - no layout scaffolding.
 *
 * See: clients/how_to_setup_ralph_loops/how-to-build-new-module.md
 */
export const ModulePageLayout: React.FC<ModulePageLayoutProps> = ({
  moduleConfig,
  layoutOptions,
  children,
}) => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const { isAuthenticated } = useAuth();
  const [breadcrumbHidden, setBreadcrumbHidden] = useState(false);

  const maxW = layoutOptions?.maxW ?? "container.xl";
  const py = layoutOptions?.py ?? 8;
  const noContainer = layoutOptions?.noContainer ?? false;
  const noBreadcrumb = layoutOptions?.noBreadcrumb ?? false;
  const noFooter = layoutOptions?.noFooter ?? false;
  const fillViewport = layoutOptions?.fillViewport ?? false;

  return (
    <LayoutChromeContext.Provider value={{ setBreadcrumbHidden }}>
      <Box
        bg={bgMain}
        display="flex"
        flexDirection="column"
        {...(fillViewport ? { h: "100dvh", overflow: "hidden" } : { minH: "100vh" })}
      >
        {/* data-app-navbar marker lets pages hide the global navbar at runtime
            (e.g. org-chart full-screen mode toggles a body class that CSS-targets
            this attribute). */}
        <Box data-app-navbar="true">
          <NavbarWithCallToAction />
        </Box>
        {/* Module navigation is a floating launcher (the breadcrumb bar is retired); signed-in only. */}
        {!noBreadcrumb && !breadcrumbHidden && isAuthenticated && <ModuleNavLauncher moduleConfig={moduleConfig as any} />}
        {noContainer ? (
          <Box
            flex="1"
            {...(fillViewport ? { minH: 0, overflow: "hidden", display: "flex", flexDirection: "column" } : {})}
          >
            {children}
          </Box>
        ) : (
          <Container maxW={maxW} px={{ base: 4, md: 8 }} py={py} flex="1">
            {children}
          </Container>
        )}
        {!noFooter && <FooterWithFourColumns />}
      </Box>
    </LayoutChromeContext.Provider>
  );
};

export default ModulePageLayout;
